/**
 * CompanyDetail.tsx — F12.M Etap D Part 2 (Companies fullscreen, B1).
 *
 * Strona szczegółów Firmy — analog do `ContractorDetail.tsx`. Refactor z
 * `CompanyDetailDrawer` (modal sliding side panel) na pełną stronę z routem
 * `/companies/:id` (edit) i `/companies/new` (create).
 *
 * Layout:
 *   header (nazwa + status badge + akcje Edit/Save/Cancel/Delete)
 *   tabs nav (6 zakładek)
 *   per-tab content
 *
 * Tabs:
 *   - data — CompanyDataTab (dynamic rendering z layout_config companies)
 *   - addresses — AddressesDynamicTab (modal pattern, polymorphic /api/addresses)
 *   - bankAccounts — BankAccountsDynamicTab (modal pattern, reuse z CompanyDetailDrawer)
 *   - org_units — OrgUnitsTab (drzewo jednostek organizacyjnych, F24 Pakiet 0, reuse z
 *     .orgUnits.tsx, dawniej „departments"/DepartmentsTab)
 *   - audit — SystemModuleAuditTab (moduleSlug='companies')
 *
 * Save flow — JEDEN zapis (pola dodatkowe nie mają osobnej zakładki ani osobnego PUT):
 *   - create: JEDEN POST /companies (pola + extValues w tym samym body)
 *   - edit: JEDEN PATCH /companies/:id (pola + extValues)
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ArrowLeft, Pencil, Save, Trash2, X as XIcon } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  buildCompanyCoreRecordValues,
  CompanyDataTab,
  type CompanyFormData,
  EMPTY_COMPANY_FORM,
} from '@/components/companies/CompanyDataTab';
import { companyEngineConfig, type CompanyExtra } from '@/components/companies/companyEngineConfig';
import { type Company } from '@/components/CompanyDetailDrawer';
import { OrgUnitsTab } from '@/components/CompanyDetailDrawer.orgUnits';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { tryRenderLayoutElement } from '@/components/fields/LayoutElementRegistry';
import { PLACEHOLDER_TYPES, RecursiveFormRenderer } from '@/components/form/RecursiveFormRenderer';
import { IconButton } from '@/components/IconButton';
import { collectEngineFieldErrors } from '@/components/kartoteki/engine/engine-config';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';
import { NewRecordDefaultsStatus } from '@/components/record-defaults/NewRecordDefaultsStatus';
import { Spinner } from '@/components/Spinner';
import { Tabs } from '@/components/Tabs';
import { TENANT_ME_QUERY_KEY } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useFieldErrors } from '@/hooks/useFieldErrors';
import {
  applyCoreDefaults,
  type NewRecordDefaults,
  numberAsString,
  useNewRecordDefaults,
} from '@/hooks/useNewRecordDefaults';
import { useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import { useSystemModuleFields } from '@/lib/ext-list-columns';
import {
  buildFieldIssueMessages,
  buildValidationToastMessage,
  resolveFieldLabel,
} from '@/lib/validation-toast';
import { buildCompanyContextRenderers } from '@/pages/company-context-renderers';

interface CompanyPayload {
  // `extValues` = surowe wartości pól dodatkowych, dołączone przez serwer do odpowiedzi GET rekordu.
  company: Company & { extValues?: Record<string, unknown> };
}

/**
 * Formularz NOWEJ firmy: wartości startowe z kodu (`EMPTY_COMPANY_FORM`) nadpisane wartościami
 * domyślnymi pól (Ustawienia kartoteki → Pola). Stawka VAT jest w formularzu tekstem (liczba →
 * tekst), a termin płatności liczbą.
 */
function buildCreateForm(core: NewRecordDefaults['core']): CompanyFormData {
  return applyCoreDefaults(EMPTY_COMPANY_FORM, core, { defaultVatRate: numberAsString });
}

export function CompanyDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const isCreate = !id || id === 'new';
  const navigate = useNavigate();
  // Powrót na listę niesie jej kontekst — patrz useWithMenuItem.
  const withMenuItem = useWithMenuItem();
  const toast = useToast();
  const queryClient = useQueryClient();
  const fe = useFieldErrors();

  const [searchParams, setSearchParams] = useSearchParams();
  // F12.N E1.T5b: layout w nowym kształcie + role context dla filtrowania zakładek.
  const { layoutConfig, myRoleId, isSuperUser } = useSystemModuleActiveLayout('companies');
  const initialTab = searchParams.get('tab') ?? 'data';
  const [tab, setTabRaw] = useState<string>(initialTab);

  // F12.N D4 save-and-continue fix: synchronizuj `tab` z URL — patrz ContractorDetail.tsx
  // dla pełnego opisu (React Router 6 nie remountuje na SPA navigate `/new → /:id`).
  useEffect(() => {
    const urlTab = searchParams.get('tab') ?? 'data';
    if (urlTab !== tab) setTabRaw(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // F12.N D4 save-and-continue — patrz ContractorDetail.tsx dla pełnego opisu wzorca.
  const pendingTabRef = useRef<string | null>(null);

  const setTab = (next: string) => {
    if (isCreate && tab === 'data' && next !== 'data' && !saveMutation.isPending) {
      pendingTabRef.current = next;
      // Format-gate przez handleSave — gdy blokuje, nie zostawiaj pendingTaba.
      if (!handleSave()) pendingTabRef.current = null;
      return;
    }
    setTabRaw(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'data') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const [editing, setEditing] = useState(isCreate);
  const [form, setForm] = useState<CompanyFormData>(EMPTY_COMPANY_FORM);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [initialPrimary, setInitialPrimary] = useState(false);

  // F12.N D4 save-and-continue: tabs wyciągnięte z layoutu — dla osobnego `<Tabs>` w trybie create.
  // T2: element `tabs` może NIE być pierwszy (przed nim elementy view-level) — szukamy po typie.
  const tabsFromLayout = useMemo(() => {
    const tabsElement = layoutConfig?.elements?.find((el) => el.type === 'tabs');
    if (tabsElement?.type === 'tabs' && tabsElement.tabs) {
      return tabsElement.tabs
        .filter((tt) => !tt.hidden)
        .slice()
        .sort((a, b) => (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity));
    }
    return null;
  }, [layoutConfig]);

  // T2: elementy view-level (rodzeństwo `tabs`) renderowane NAD paskiem zakładek w create.
  const viewLevelFromLayout = useMemo(
    () => layoutConfig?.elements?.filter((el) => el.type !== 'tabs') ?? [],
    [layoutConfig],
  );

  const detailQ = useQuery({
    queryKey: ['company', id],
    queryFn: async () => {
      const res = await api.get<CompanyPayload>(`/companies/${id}`);
      return res.data;
    },
    enabled: !isCreate,
  });

  // Wartości domyślne pól (Ustawienia kartoteki → Pola) dla formularza nowej firmy — pola
  // pokazujemy dopiero po ich wczytaniu (patrz `NewRecordDefaultsStatus` niżej).
  const recordDefaults = useNewRecordDefaults('companies', isCreate);

  // F12.M D.2: pola dodatkowe — stan lokalny zasilany wartością z odpowiedzi GET rekordu
  // (`company.extValues`, bez osobnego żądania). Pełny kontrakt synchronizacji (w tym ochrona
  // niezapisanych edycji przed odświeżeniem w tle) w nagłówku `useRecordExtValues.ts`. Przy
  // tworzeniu „wartością serwera" są wartości domyślne pól dodatkowych.
  const { extValues, handleExtChange, snapshotEdits, markSaved, discardEdits } = useRecordExtValues(
    {
      recordId: isCreate ? undefined : id,
      serverExtValues: isCreate ? recordDefaults.ext : detailQ.data?.company.extValues,
      serverVersion: detailQ.dataUpdatedAt,
    },
  );

  // Widgety zależne od kontekstu (company_members_view) — patrz nagłówek
  // `company-context-renderers.tsx`. UWAGA: na trasie `/companies/new` `id` route
  // param to string `'new'`, NIE `undefined` (stąd `isCreate = !id || id === 'new'`
  // linia 68) — przekazanie surowego `id` dałoby fabryce prawdziwą wartość
  // `companyId="new"` i strzeliłoby żądaniem do nieistniejącego rekordu. W trybie
  // create przekazujemy jawnie `null` (mirror `CommercialDocDetail.tsx` — `recordId
  // = isCreate ? null : id`), żeby guard fabryki „brak firmy → nic nie renderuj"
  // faktycznie się odpalał.
  const companyContextRenderers = useMemo(
    () => buildCompanyContextRenderers(isCreate ? null : (id ?? null)),
    [isCreate, id],
  );

  // Silnik formatek (keystone unifikacja-formatek — mirror ContractorDetail.tsx) — 14 pól
  // core firmy przez `companyEngineConfig`. `extra.initialPrimary` niesie to, co wcześniej
  // trzymał osobny prop `initialPrimary` na CompanyDataTab (D13 lock pola `is_primary`) —
  // silnik wymaga JEDNEGO kierunku przepływu (strona → runtime → renderer).
  const { renderField, fields: kartotekaFields } = useKartotekaRenderField<
    CompanyFormData,
    CompanyExtra
  >(companyEngineConfig, {
    form,
    onChange: setForm,
    extValues,
    onExtChange: handleExtChange,
    editable: editing,
    isCreate,
    hasError: (key) => !!fe.get(String(key)),
    getError: (key) => fe.get(String(key)),
    clearError: (key) => fe.clear(String(key)),
    extra: { initialPrimary },
  });

  // Wartości formularza keyed po slugu (core + ext) — do oceny warunków widoczności
  // pól/bloków z kreatora. Przekazywane do CompanyDataTab i RecursiveFormRenderer.
  const layoutFormData = useMemo<Record<string, unknown>>(
    () => ({ ...buildCompanyCoreRecordValues(form), ...extValues }),
    [form, extValues],
  );

  const companyFieldsQ = useSystemModuleFields('companies');
  const companyFieldLabel = (field: string) => resolveFieldLabel(companyFieldsQ.data ?? [], field);

  // Sync server → local form
  useEffect(() => {
    if (isCreate) {
      // Wartości domyślne nadpisują wartości startowe z kodu (pola są ukryte do ich wczytania).
      setForm(buildCreateForm(recordDefaults.core));
      setInitialPrimary(false);
      return;
    }
    if (detailQ.data?.company) {
      const c = detailQ.data.company;
      setForm({
        legalName: c.legalName,
        shortName: c.shortName,
        taxId: c.taxId,
        vatEu: c.vatEu,
        regon: c.regon,
        krs: c.krs,
        email: c.email,
        phone: c.phone,
        website: c.website,
        brandColor: c.brandColor,
        defaultVatRate: c.defaultVatRate,
        defaultPaymentTermsDays: c.defaultPaymentTermsDays,
        isPrimary: c.isPrimary,
        isActive: c.isActive,
        groups: c.groups ?? [],
      });
      setInitialPrimary(c.isPrimary);
    }
  }, [detailQ.data, isCreate, recordDefaults.core]);

  const saveMutation = useMutation({
    mutationFn: async (data: CompanyFormData) => {
      // Czyścimy puste stringi do null (backend NIP walidator)
      const payload: CompanyFormData & { extValues?: Record<string, unknown> } = {
        ...data,
        shortName: data.shortName || null,
        taxId: data.taxId || null,
        vatEu: data.vatEu || null,
        regon: data.regon || null,
        krs: data.krs || null,
        email: data.email || null,
        phone: data.phone || null,
        website: data.website || null,
        brandColor: data.brandColor || null,
        defaultVatRate: data.defaultVatRate || null,
      };
      // Atomic-save (mirror ContractorDetail): jeden zapis — extValues jadą w TYM SAMYM body co
      // pola firmy (backend zapisuje wszystko w jednej transakcji, saveAggregate); zero osobnego
      // PUT ext-fields.
      // Migawka edycji tuż przed wysłaniem: edycja zrobiona w trakcie żądania nie zostanie zdjęta
      // przez `markSaved()` w onSuccess (patrz nagłówek `useRecordExtValues.ts`).
      snapshotEdits();
      if (Object.keys(extValues).length > 0) {
        payload.extValues = extValues;
      }

      let recordId: string;
      if (isCreate) {
        const res = await api.post<{ company: { id: string } }>(`/companies`, payload);
        recordId = res.data.company.id;
      } else {
        await api.patch(`/companies/${id}`, payload);
        recordId = id!;
      }
      return { company: { id: recordId } };
    },
    onSuccess: (data) => {
      // Udany zapis gasi podświetlenia z poprzedniej nieudanej próby (ramka + tekst).
      fe.clearAll();
      // Pola dodatkowe zapisane razem z firmą — od tej chwili wartość serwera (z odświeżonego
      // rekordu) może je nadpisać; bez tego guard niezapisanych edycji trzymałby stare wartości.
      markSaved();
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      // Przelacznik firm (CompanySwitcher) czyta firmy z /auth/me, nie z /companies —
      // musimy odswiezyc TEN klucz, inaczej nowa firma pojawia sie dopiero po F5.
      queryClient.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
      if (isCreate && data?.company?.id) {
        // F12.N D4 save-and-continue: dorzucamy ?tab=<next> jeśli zapis był wyzwolony zmianą zakładki.
        const nextTab = pendingTabRef.current;
        pendingTabRef.current = null;
        const suffix = nextTab && nextTab !== 'data' ? `?tab=${nextTab}` : '';
        toast.show({ variant: 'success', title: t('tenant.companies.toast.created') });
        // Karta „nowy" ma zamienić się w kartę zapisanego rekordu (replaceTab) —
        // reset do podglądu, bo create→detail nie remontuje strony.
        setEditing(false);
        navigate(withMenuItem(`/companies/${data.company.id}${suffix}`), {
          state: { replaceTab: true },
        });
        return;
      }
      toast.show({ variant: 'success', title: t('tenant.companies.toast.updated') });
      queryClient.invalidateQueries({ queryKey: ['company', id] });
      setEditing(false);
    },
    onError: (err) => {
      // F12.N D4 save-and-continue: błąd → reset pendingTab (admin zostaje na 'data').
      pendingTabRef.current = null;
      // Podświetlenie + tekst pod polem odrzuconym przez serwer. Kolejność komunikatu:
      // (1) polski z issue Zoda, (2) własny komunikat serwera z `details` (refine
      // NIP/REGON/KRS/kwoty mają tu gotowe polskie zdanie, bez `issues`), (3) generyk.
      const fieldMsgs = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        translate: (field, messages) =>
          fieldMsgs[field] ?? messages[0] ?? t('common.validationIssue.generic'),
      });
      const code = extractErrorCode(err);
      if (code === 'VALIDATION_ERROR') {
        const specific = buildValidationToastMessage(err, companyFieldLabel, t);
        toast.show({
          variant: 'error',
          title: specific ?? t('tenant.companies.toast.validationError'),
        });
        return;
      }
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.show({ variant: 'error', title: t('tenant.companies.toast.conflict') });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.companies.toast.saveError') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/companies/${id}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.companies.toast.deleted') });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
      navigate(withMenuItem('/companies'));
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.companies.toast.deleteError') });
    },
  });

  function handleCancel() {
    // Anulowanie gasi podświetlenia z nieudanej próby zapisu (ramka + tekst pod polem).
    fe.clearAll();
    if (isCreate) {
      navigate(withMenuItem('/companies'));
      return;
    }
    setEditing(false);
    discardEdits();
    if (detailQ.data?.company) {
      const c = detailQ.data.company;
      setForm({
        legalName: c.legalName,
        shortName: c.shortName,
        taxId: c.taxId,
        vatEu: c.vatEu,
        regon: c.regon,
        krs: c.krs,
        email: c.email,
        phone: c.phone,
        website: c.website,
        brandColor: c.brandColor,
        defaultVatRate: c.defaultVatRate,
        defaultPaymentTermsDays: c.defaultPaymentTermsDays,
        isPrimary: c.isPrimary,
        isActive: c.isActive,
        groups: c.groups ?? [],
      });
    }
  }

  // Walidacja formatu wg TYPU pola (wspólna oś silnika) przed wysyłką: e-mail/URL/
  // liczba/data dla pól core (config, po stateKey) i dodatkowych (katalog, po slugu).
  // Zwraca false gdy zablokowano zapis (callsite resetuje pendingTab), true gdy poszło.
  function handleSave(): boolean {
    const fieldErrors = collectEngineFieldErrors(
      companyEngineConfig,
      form,
      kartotekaFields,
      extValues,
      t,
    );
    if (Object.keys(fieldErrors).length > 0) {
      fe.clearAll();
      for (const [key, msg] of Object.entries(fieldErrors)) {
        fe.set(key, msg);
      }
      toast.show({ variant: 'error', title: t('common.validationIssue.formToast') });
      return false;
    }
    saveMutation.mutate(form);
    return true;
  }

  if (!isCreate && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="company-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isError) {
    return (
      <div
        className="m-6 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        data-testid="company-detail-error"
      >
        {t('tenant.companies.detail.loadError')}
      </div>
    );
  }

  // Nowa firma: pola dopiero po wczytaniu wartości domyślnych (albo komunikat z „Spróbuj
  // ponownie”). Po wszystkich hookach.
  if (isCreate && recordDefaults.status !== 'ready') {
    return <NewRecordDefaultsStatus defaults={recordDefaults} testIdPrefix="company-detail" />;
  }

  return (
    <div className="space-y-4 p-6" data-testid="company-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t('common.back')}
            data-testid="company-detail-back"
            onClick={() => navigate(withMenuItem('/companies'))}
          >
            <ArrowLeft />
          </IconButton>
          <div>
            {isCreate ? (
              <h1
                className="text-2xl font-semibold text-text-primary"
                data-testid="company-detail-create-title"
              >
                {t('tenant.companies.detail.createTitle')}
              </h1>
            ) : (
              <>
                <h1 className="text-2xl font-semibold text-text-primary">{form.legalName}</h1>
                <div className="mt-1 flex items-center gap-3 text-xs text-text-tertiary">
                  {form.isPrimary && (
                    <span className="rounded bg-accent-50 px-2 py-0.5 text-accent-700">
                      {t('tenant.companies.detail.primaryBadge')}
                    </span>
                  )}
                  {!form.isActive && (
                    <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-700">
                      {t('tenant.companies.detail.inactiveBadge')}
                    </span>
                  )}
                  {form.taxId && <span>NIP: {form.taxId}</span>}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {editing ? (
            <>
              <Button
                onClick={handleCancel}
                variant="secondary"
                leftIcon={<XIcon size={14} aria-hidden="true" />}
                disabled={saveMutation.isPending}
                data-testid="company-detail-cancel-btn"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => handleSave()}
                variant="primary"
                leftIcon={<Save size={14} aria-hidden="true" />}
                loading={saveMutation.isPending}
                disabled={saveMutation.isPending}
                data-testid="company-detail-save-btn"
              >
                {isCreate ? t('common.create') : t('common.save')}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => setConfirmDelete(true)}
                variant="danger"
                leftIcon={<Trash2 size={14} aria-hidden="true" />}
                disabled={form.isPrimary}
                data-testid="company-detail-delete-btn"
              >
                {t('common.delete')}
              </Button>
              <Button
                onClick={() => setEditing(true)}
                leftIcon={<Pencil size={14} aria-hidden="true" />}
                data-testid="company-detail-edit-btn"
              >
                {t('common.edit')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* F12.N E1.T5b: tryb create + brak layoutu → sam CompanyDataTab bez top-bara.
          W trybie edit z layoutem → RecursiveFormRenderer rendruje zakładki +
          tabRenderers delegują content per seed-slug. */}
      {/* F12.N D4 save-and-continue: w trybie create renderujemy OSOBNY `<Tabs>`
          nad stabilnym CompanyDataTab — klik na inną zakładkę uruchamia niejawny POST.
          Patrz ContractorDetail.tsx dla pełnego wzorca. */}
      {isCreate ? (
        <>
          {viewLevelFromLayout.map((el) => {
            // Mirror bramki z CompanyDataTab.tsx / ContractorDataTab.tsx — elementy
            // zależne od kontekstu (company_members_view/…) muszą iść przez
            // contextRenderers, inaczej spadają do martwego placeholdera z
            // generycznego rejestru. Dziś ta ścieżka jest nieosiągalna przez UI
            // kreatora (żaden zarejestrowany element tego typu nie ma
            // `viewLevel: true`), ale bramka jest tu dla spójności/defense-in-depth.
            const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
              ? companyContextRenderers[el.type]
              : undefined;
            return (
              <div key={el.id} className="mb-4">
                {contextRenderer
                  ? contextRenderer({
                      element: { id: el.id, type: el.type, settings: el.settings },
                    })
                  : tryRenderLayoutElement({
                      element: {
                        id: el.id,
                        type: el.type,
                        settings: (el.settings ?? {}) as Record<string, unknown>,
                      },
                      children: [],
                      mode: 'form',
                    })}
              </div>
            );
          })}
          {tabsFromLayout && tabsFromLayout.length > 1 && (
            <Tabs
              items={tabsFromLayout.map((tt) => ({
                value: tt.slug,
                label: tt.title,
                content: null,
              }))}
              value={tab}
              onChange={setTab}
              idPrefix="company-create-tabs"
              // mb-8 = dawny mb-4 + 16 px pustego panelu, którego Tabs przy `content: null` już nie rysuje.
              className="mb-8"
            />
          )}
          <CompanyDataTab
            renderField={renderField}
            contextRenderers={companyContextRenderers}
            formData={layoutFormData}
          />
        </>
      ) : !layoutConfig ? (
        <CompanyDataTab
          renderField={renderField}
          contextRenderers={companyContextRenderers}
          formData={layoutFormData}
        />
      ) : (
        <RecursiveFormRenderer
          layoutConfig={layoutConfig}
          activeTabId={tab}
          onTabChange={setTab}
          myRoleId={myRoleId}
          isSuperUser={isSuperUser}
          renderField={renderField}
          contextRenderers={companyContextRenderers}
          formData={layoutFormData}
          tabRenderers={{
            data: () => (
              <CompanyDataTab
                renderField={renderField}
                contextRenderers={companyContextRenderers}
                formData={layoutFormData}
              />
            ),
            // „Adresy" i „Konta bankowe" NIE są już zaszyte po slugu — renderują się jako
            // przenośne bloki (`company_addresses_view` / `company_bank_accounts_view`) przez
            // companyContextRenderers, więc można je ułożyć/ukryć w dowolnej zakładce (blokify Firm).
            // „Historia zmian" NIE jest już zaszyta po slugu — renderuje się jako przenośny blok
            // (`company_audit_view`, z fallbackiem na generyczny `audit_log_view`) przez
            // companyContextRenderers, więc jest usuwalna i dodawalna z palety („nic nie ginie").
            // „Pola dodatkowe" nie mają osobnej zakładki: pola układa w „Dane" silnik formatek.
            // Struktura (org_units) bez zmian.
            org_units: () => (id ? <OrgUnitsTab companyId={id} /> : null),
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('common.confirmDelete')}
        message={form.legalName}
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onClose={() => setConfirmDelete(false)}
      />
    </div>
  );
}
